import {
  fetchCurrentPlan,
  getBeneficiaryInfo,
  getEnvironment,
  initiateLogin,
  isCurrentPlan,
  isOutsideOpenEnrollment,
  logError,
  makePlanLongId,
} from ".";
import {
  UserLanguage,
  FullPlanId,
  MctToastType,
  PlanDetailsPageSection,
  IdParts,
  ActionType,
  Action,
  Envs,
  AppState,
  MBPTargets,
  MBPLandingParams,
} from "../@types";
import routes from "../app/routes";
import URI from "urijs";
import { RouteComponentProps } from "react-router";
import { Dispatch } from "react";
import {
  getCounties,
  getCounty,
  getBeneDrugs,
  getBenePharmacies,
} from "../api";
import { getPharmacyTypeFromSavedPharmacies } from "./beneficiaryInfoHelpers";
import { parseDegradedIntegrations } from "./loginHelpers";
import { setMbpReferrerUri } from "./mbpReferrerHelpers";
import { fetchPlanIdForNextYear } from "./planHelpers";
import { LDFlagSet } from "launchdarkly-js-sdk-common";

const makeFullUrl = (
  baseRoute: string,
  language: UserLanguage,
  year: string,
  section: string
): string => {
  return `${baseRoute}?year=${year}&lang=${language}#${section}`;
};

export const getPlanDetailsStarRatingsRoute = (
  plan: FullPlanId,
  language: UserLanguage,
  year: string
): string => {
  return makeFullUrl(
    routes.planDetails.replace(":id", makePlanLongId(plan)),
    language,
    year,
    PlanDetailsPageSection.STAR_RATINGS
  );
};

export const getToastRoute = (
  currentRoute: string,
  toastType: MctToastType
): string => {
  const curr = new URI(currentRoute);
  curr.addQuery("mctt", toastType);

  return curr.toString();
};

export const getRouteName = (r: string): string => {
  r = r.replace(/\/MEDIGAP_PLAN_TYPE_.+/, "");
  r = r.replace(/\/\d.+/, "");
  r = r.replace(/states\/[A-Z]{2}/, "states/");

  const routeNames = {
    [routes.coverageWizard.info]: "Coverage Wizard - Info",
    [routes.coverageWizard.landingPage]: "Coverage Wizard - Landing",
    [routes.coverageWizard.options]: "Coverage Wizard - Options",
    [routes.pap.landingPage]: "PAP - Home",
    [routes.pap.plans.replace("/:drug", "")]: "PAP - Results",
    [routes.pace.landingPage]: "PACE - Landing",
    [routes.pace.plans.replace(":state", "")]: "PACE - Plans",
    [routes.medigap.landingPage]: "Medigap - Find a Policy",
    [routes.medigap.planDetails.replace(
      "/:medigapPlanType",
      ""
    )]: "Medigap - Plan Details",
    [routes.medigap.plans]: "Step 1: Decide what plans you want",
    [routes.medigap.policies.replace(
      "/:medigapPlanType",
      ""
    )]: "Step 2: Pick your Policy",
    [routes.managePrescriptions]: "Find Plans - Add Drugs",
    [routes.prescriptionsList]: "Find Plans - Add Drugs",
    [routes.pharmacy]: "Find Plans - Add Pharmacy",
    [routes.searchResults]: "Find Plans - Search Results",
    [routes.questionRouting]: "Find Plans - Enter Your Information",
    [routes.drugSearchPreferences]: "Find Plans - See Drug Costs",
    [routes.enroll]: "OEC - Enrollment",
    [routes.planDetails.replace("/:id", "")]: "Find Plans - Plan Details",
    [routes.sanctionedPlans]: "Find Plans - Sanctioned Plans",
    [routes.spap.landingPage]: "SPAP - Home",
    [routes.spap.plans.replace(":state", "")]: "SPAP - Results",
    [routes.pdePage]: "Find Plans - LIS Select Drugs",
    ["/"]: "Find Plans - Landing",
  };

  return routeNames[r] || r;
};

export const getRedirectForMedigapOrPlanFinder = (url: URL): string | null => {
  let redirectUrl = null;

  const medigapPath = "medigap-supplemental-insurance-plans";
  const planComparePath = "plan-compare";

  const medigapRoutes = Object.values(routes.medigap).map(route => {
    const matches = route.match(/m\/(.*?)\//);
    const value = matches ? `/m/${matches[1]}` : route;

    return value;
  });

  // Get a list of all non-medigap routes (some of which are in nested objects)
  // and replace their tokens with empty strings
  const planCompareRoutes: unknown[] = [];
  Object.keys(routes)
    .filter((r): r is string => r !== "medigap")
    .forEach(route => {
      if (typeof routes[route] === "string") {
        planCompareRoutes.push(routes[route].replace("/:id", ""));
      } else {
        Object.values(routes[route]).forEach(r =>
          planCompareRoutes.push((r as string).replace(/:state|:drug/, ""))
        );
      }
    });

  const { pathname, hash } = url;

  // Remove the #, query string, and additional "/" after m/route
  let formattedHash = hash.replace("#", "").replace(/\?.+/gm, "");

  const matches = formattedHash.match(/m\/(.*?)\//);
  formattedHash = matches ? `/m/${matches[1]}` : formattedHash;

  const strippedPath = pathname.replace(/\//gm, "");

  if (
    strippedPath === medigapPath &&
    planCompareRoutes.includes(formattedHash)
  ) {
    redirectUrl = `${url.origin}/${planComparePath}/${url.hash}`;
  } else if (
    strippedPath === planComparePath &&
    medigapRoutes.includes(formattedHash)
  ) {
    redirectUrl = `${url.origin}/${medigapPath}/${url.hash}`;
  } else if (
    strippedPath === medigapPath &&
    !medigapRoutes.includes(formattedHash)
  ) {
    redirectUrl = `${url.origin}/${medigapPath}/#/m`;
  }

  return redirectUrl;
};

/**
 * Get the first segment of a route path, no slashes and no params
 * @param path - A path listed in app routes, which may contain slash-delimited
 *               segments and params
 * @returns - The first segment of the path, only
 */
export const getBasePath = (path: string): string =>
  path.replace(/^\/*([\d|\w|\-|_]*).*$/, (_match, p1) => p1);

/**
 * Determine whether the current route should display the Consistent Header
 * @param location - A location object passed from a routed component, e.g.
 * @param hideGlobalHeaderPaths - An array of route paths for routes that shouldn't
 *                                show the Global (Consistent) Header
 * @returns - Whether the current route should display the CH
 */
export const shouldDisplayGlobalHeader = (
  location: Partial<Location>,
  hideGlobalHeaderPaths: string[]
): boolean =>
  !hideGlobalHeaderPaths
    .map(getBasePath)
    .some(path => location?.pathname?.includes(path));

/**
 * Create a link to plan details interpolating the :id variable with a plan long id
 * and adding needed query parameters
 * @param plan
 * @param language
 * @returns A formatted link to plan details
 */
export const makePlanDetailsUrl = ({
  plan,
  yearValue = "contract_year",
  language,
}: {
  plan: IdParts;
  yearValue?: "contract_year" | "coverage_year";
  language: UserLanguage;
}): string =>
  `${routes.planDetails.replace(
    ":id",
    makePlanLongId(plan)
  )}?lang=${language}&year=${plan[yearValue]}`;

/**
 * UserActivityMonitor passes a concatenated location.path + location.search string
 * in the property `from` on `history.location.state`. If this property is not defined, do not
 * attempt to call `history.replace(from)`
 */
export const sendToFromIfExists = (history: RouteComponentProps["history"]) => {
  if (history.location.state) {
    const { from } = history.location.state as {
      from?: string;
    };
    if (from) {
      history.replace(from);
    }
  }
};

const handleMbpFailure = ({
  dispatch,
  history,
}: {
  dispatch: Dispatch<Action>;
  history: RouteComponentProps["history"];
}): void => {
  dispatch({ type: ActionType.UPDATE_MBP_HANDOFF_FAILED, payload: true });
  history.replace("/");
};

export const handleMbpLandingRoute = async ({
  dispatch,
  search,
  flags,
  history,
  state,
}: {
  dispatch: Dispatch<Action>;
  search: string;
  flags: LDFlagSet;
  history: RouteComponentProps["history"];
  state: AppState;
}): Promise<void> => {
  const {
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
    tmpFeEnableStandAlonePages,
  } = flags;

  const outsideOpenEnrollment = isOutsideOpenEnrollment({
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  });

  const { language } = state;

  dispatch({
    type: ActionType.UPDATE_MBP_HANDOFF_FAILED,
    payload: false,
  });

  const tempRoute = new URI("/");

  const searchObj = URI.parseQuery(search) as MBPLandingParams;

  const code = searchObj.req_token;

  // If we are using SLSx and there isn't a req_token, we need to send the user to SSO
  if (!code) {
    const isLocal = getEnvironment() === Envs.local;

    return initiateLogin(
      language,
      isLocal
        ? "https://localhost/mbp"
        : `${window.location.origin}/plan-compare/mbp`,
      searchObj
    );
  }

  let params: MBPLandingParams = {};

  const { relay } = searchObj;

  if (relay) {
    try {
      // relay should be a base64-encoded string
      const decodedRelay: MBPLandingParams = JSON.parse(atob(relay));

      params = decodedRelay;
    } catch (e) {
      logError("Failed to parse the relay (MBPLandingPage)", e);

      return handleMbpFailure({ dispatch, history });
    }
  }

  const {
    fips,
    zipCode,
    target,
    planYear: contract_year,
    planId: plan_id,
    contractId: contract_id,
    segmentId: segment_id,
    lang = UserLanguage.ENGLISH,
    mbp_redirect,
  } = params;

  if (tmpFeEnableStandAlonePages) {
    setMbpReferrerUri(dispatch, mbp_redirect);
  }

  if (!code || !target) {
    return handleMbpFailure({ dispatch, history });
  }

  let finalFips = fips;

  dispatch({ type: ActionType.UPDATE_LANGUAGE, payload: lang });

  if (!fips && zipCode) {
    dispatch({ type: ActionType.UPDATE_ZIPCODE, payload: zipCode });

    await getCounties(String(zipCode)).then(counties => {
      if (counties.length === 1) {
        finalFips = counties[0].fips;
        dispatch({
          type: ActionType.UPDATE_COUNTY,
          payload: counties[0],
        });
      }
    });
  }

  if (finalFips) {
    dispatch({ type: ActionType.UPDATE_FIPS, payload: finalFips });
    await getCounty(finalFips).then(county =>
      dispatch({ type: ActionType.UPDATE_COUNTY, payload: county })
    );
  }

  let nextYearPlanId: FullPlanId | undefined;

  try {
    const { headers, beneficiary: bene } = await getBeneficiaryInfo(code);

    const degradedIntegrations = parseDegradedIntegrations(headers);

    if (degradedIntegrations.length) {
      dispatch({
        type: ActionType.UPDATE_DEGRADED_INTEGRATIONS,
        payload: degradedIntegrations,
      });
    }

    if (bene) {
      dispatch({ type: ActionType.ADD_BENEFICIARY, payload: bene });
      dispatch({
        type: ActionType.UPDATE_LIS,
        payload: bene.lis_level,
      });
      dispatch({
        type: ActionType.UPDATE_FUTURE_LIS,
        payload: bene.future_lis_level,
      });

      if (bene.current_coverage.length) {
        if (!outsideOpenEnrollment) {
          nextYearPlanId = await fetchPlanIdForNextYear(
            bene.current_coverage[0],
            "MBP Landing Page"
          );
        }
        fetchCurrentPlan(
          bene,
          [],
          bene.lis_level,
          dispatch,
          nextYearPlanId,
          bene.future_lis_level
        );
      }

      try {
        const drugs = await getBeneDrugs();

        dispatch({
          type: ActionType.SET_PRESCRIPTIONS,
          payload: drugs,
        });

        if (target === MBPTargets.PRESCRIPTIONS) {
          tempRoute.href(
            `${routes.prescriptionsList}?fips=${finalFips ? finalFips : ""}`
          );
        }
      } catch (e) {
        logError("Could not get beneficiary's drugs (MBPLandingPage)", e);

        if (target === MBPTargets.PRESCRIPTIONS) {
          return handleMbpFailure({ dispatch, history });
        }
      }

      try {
        const { pharmacies, mail_order } = await getBenePharmacies();
        dispatch({
          type: ActionType.SET_PHARMACIES,
          payload: pharmacies,
        });

        dispatch({
          type: ActionType.UPDATE_PHARMACY_TYPE,
          payload: getPharmacyTypeFromSavedPharmacies(pharmacies, mail_order),
        });

        if (target === MBPTargets.PHARMACIES) {
          tempRoute.href(routes.managePharmacies);
        }
      } catch (e) {
        logError("Failed to get beneficiary pharmacies (MBPLandingPage)", e);

        if (target === MBPTargets.PHARMACIES) {
          return handleMbpFailure({ dispatch, history });
        }
      }

      if (target === MBPTargets.PLAN || target === MBPTargets.STAR_RATINGS) {
        if (contract_year && plan_id && segment_id && contract_id) {
          const idParts = {
            contract_year,
            plan_id,
            segment_id,
            contract_id,
          };
          // If we are not outside open enrollment and the id passed in is different from
          // what we have as the bene's current coverage, we defer to the passed
          // in id when determining the id of the next year's plan
          if (
            !outsideOpenEnrollment &&
            !isCurrentPlan(bene.current_coverage[0], idParts)
          ) {
            nextYearPlanId = await fetchPlanIdForNextYear(
              idParts,
              "MBP Landing Page"
            );
          }
          const urlPlanId = nextYearPlanId
            ? makePlanLongId(nextYearPlanId)
            : makePlanLongId(idParts);

          if (target === MBPTargets.PLAN) {
            tempRoute.href(routes.planDetails.replace(":id", urlPlanId));
          } else if (target === MBPTargets.STAR_RATINGS) {
            tempRoute.href(
              getPlanDetailsStarRatingsRoute(
                nextYearPlanId ? nextYearPlanId : idParts,
                language,
                contract_year
              )
            );
          }
        } else {
          return handleMbpFailure({ dispatch, history });
        }
      }
    }

    if (target === MBPTargets.GATEWAY) {
      tempRoute.href(routes.beneLanding);
    }

    if (target === MBPTargets.ENROLL_STATUS) {
      tempRoute.href(routes.enrollmentStatus);
    }

    return history.replace(tempRoute.addQuery({ lang }).toString());
  } catch (e) {
    logError("Could not get beneficiary (MBPLandingPage)", e);
    return handleMbpFailure({ dispatch, history });
  }
};
